/**
 * Validation.
 *
 * Two rules matter more than the individual checks:
 *
 *  - Validation runs over the **visible** set only. A required field the display logic has
 *    hidden must never block submission, or the technician is stuck on a question they cannot
 *    see and the only escape is to invent an answer to an unrelated question.
 *  - A prefilled value is still validated. If Salesforce holds something the schema says is
 *    impossible, that is worth surfacing, not hiding.
 *
 * ─── AND A THIRD, ADDED WITH THE MULTI-LANGUAGE WORK ────────────────────────────────────────────
 *
 * **These are the only strings in the app that block a technician from submitting**, which is why they
 * were the first component strings externalised (`src/i18n/uiStrings.ts`, `ui.validate.*`). A required
 * field with an English message a technician cannot read is not an inconvenience: there is no way
 * forward from it. Every message here is now a key plus parameters, resolved through `Messages`.
 *
 * Three properties of how that was done, because each one is load-bearing:
 *
 *  1. **`messages` is optional and defaults to English**, so every existing caller — the renderer, the
 *     review step, the tests — is unchanged and the English output is byte-identical to the literals it
 *     replaced. The locale is threaded in later by whoever adds the translator context to the shell.
 *  2. **`Problem` carries `messageKey` and `messageParams` alongside `message`.** A screen that wants
 *     to re-render the same problem in a newly-chosen locale can, without re-running validation, and a
 *     test can assert *which* message fired rather than matching on English prose.
 *  3. **Counts go through plural categories, not `n === 1`.** `At least 1 photos are needed` was a real
 *     bug in the English source; French additionally puts zero in the `one` category. See
 *     `src/i18n/message.ts` for the measured table.
 */

import { compilePattern, computeVisibility, isAnswered } from './logic.ts';
import type { Answer, Field, FormSchema, FormState, PhotoRef, Section, SerialValue } from './types.ts';
import type { Visibility } from './logic.ts';
import { englishMessages } from '../i18n/messages.ts';
import type { Messages } from '../i18n/messages.ts';
import { formatList } from '../i18n/message.ts';
import type { MessageParams } from '../i18n/message.ts';
import { VALIDATION_KEYS } from '../i18n/uiStrings.ts';

export interface Problem {
  readonly fieldId: string;
  readonly sectionId: string;
  readonly sectionTitle: string;
  readonly label: string;
  readonly kind: 'required' | 'invalid';
  /** Resolved for the active locale. English when no `Messages` was supplied. Never empty. */
  readonly message: string;
  /** The `ui.validate.*` key `message` came from. Lets a locale switch re-render without re-validating. */
  readonly messageKey: string;
  /** Values interpolated into the message, when it takes any. */
  readonly messageParams?: MessageParams;
}

/** One thing wrong with one field, before it is located in the form. */
export interface FieldProblem {
  readonly kind: 'required' | 'invalid';
  readonly message: string;
  readonly messageKey: string;
  readonly messageParams?: MessageParams;
}

function asPhotos(value: Answer | undefined): readonly PhotoRef[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is PhotoRef => typeof v === 'object' && v !== null && 'blobId' in v);
}

function asSelections(value: Answer | undefined): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

function asSerial(value: Answer | undefined): SerialValue | undefined {
  if (typeof value === 'object' && value !== null && 'serial' in value) return value as SerialValue;
  return undefined;
}

/**
 * Everything wrong with one field's current value. Empty array means fine.
 *
 * `messages` defaults to English, so the two-argument call every existing caller makes is unchanged and
 * produces the same sentences it always did.
 *
 * ⚠️ **`rules.patternMessage` is NOT looked up here**, and that is correct rather than an omission: it
 * is a *schema* string, already externalised as `schema.<id>.field.<id>.patternMessage`, and
 * `localiseSchema()` translates it into the schema copy the renderer holds. Looking it up again here
 * would be a second, competing translation of the same string. Only the generic fallback — used when a
 * schema supplies a pattern but no message — comes from the component catalogue.
 */
export function checkField(field: Field, value: Answer | undefined, messages: Messages = englishMessages()): FieldProblem[] {
  const problems: FieldProblem[] = [];
  if (field.kind === 'note') return problems;

  const say = (kind: 'required' | 'invalid', key: string, params?: MessageParams): FieldProblem => ({
    kind,
    message: messages.m(key, params),
    messageKey: key,
    ...(params !== undefined ? { messageParams: params } : {}),
  });

  const answered = isAnswered(value);

  if (field.required && !answered) {
    const key =
      field.kind === 'photo' ? VALIDATION_KEYS.requiredPhoto :
      field.kind === 'serial' ? VALIDATION_KEYS.requiredSerial :
      VALIDATION_KEYS.requiredAnswer;
    problems.push(say('required', key));
    // No point range-checking a blank.
    return problems;
  }
  if (!answered) return problems;

  const rules = field.validate;

  if (field.kind === 'photo') {
    const photos = asPhotos(value);
    if (rules?.minPhotos !== undefined && photos.length < rules.minPhotos) {
      problems.push(say('invalid', VALIDATION_KEYS.photoMin, { count: rules.minPhotos }));
    }
    if (rules?.maxPhotos !== undefined && photos.length > rules.maxPhotos) {
      problems.push(say('invalid', VALIDATION_KEYS.photoMax, { count: rules.maxPhotos }));
    }
    return problems;
  }

  if (field.kind === 'multi') {
    const chosen = asSelections(value);
    if (rules?.minSelections !== undefined && chosen.length < rules.minSelections) {
      problems.push(say('invalid', VALIDATION_KEYS.choiceMin, { count: rules.minSelections }));
    }
    if (rules?.maxSelections !== undefined && chosen.length > rules.maxSelections) {
      problems.push(say('invalid', VALIDATION_KEYS.choiceMax, { count: rules.maxSelections }));
    }
    if (field.options) {
      const allowed = new Set(field.options);
      const stray = chosen.filter((c) => !allowed.has(c));
      if (stray.length > 0) {
        /*
         * The stray values are Salesforce picklist VALUES and are interpolated verbatim, never
         * translated — `localiseSchema.ts` explains why translating an option breaks every non-English
         * submission. `formatList` localises only the CONNECTIVE between them (de: "Alpha und Beta"),
         * so each value still passes through byte-identical for the technician to match against what
         * the option literally says.
         */
        problems.push(say('invalid', VALIDATION_KEYS.notAnOption, { values: formatList(messages.locale, stray) }));
      }
    }
    return problems;
  }

  if (field.kind === 'single' && field.options) {
    const chosen = typeof value === 'string' ? value : String(value ?? '');
    if (!field.options.includes(chosen)) {
      problems.push(say('invalid', VALIDATION_KEYS.notAnOption, { values: chosen }));
    }
  }

  if (field.kind === 'number') {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) {
      problems.push(say('invalid', VALIDATION_KEYS.notANumber));
      return problems;
    }
    if (rules?.integer && !Number.isInteger(n)) {
      problems.push(say('invalid', VALIDATION_KEYS.notWhole));
    }
    if (rules?.min !== undefined && n < rules.min) {
      problems.push(say('invalid', VALIDATION_KEYS.numberMin, { min: rules.min }));
    }
    if (rules?.max !== undefined && n > rules.max) {
      problems.push(say('invalid', VALIDATION_KEYS.numberMax, { max: rules.max }));
    }
    return problems;
  }

  // Text-shaped kinds: text, textarea, time, date, serial.
  const serial = asSerial(value);
  const text = serial ? serial.serial : typeof value === 'string' ? value : String(value ?? '');

  if (rules?.minLength !== undefined && text.trim().length < rules.minLength) {
    problems.push(say('invalid', VALIDATION_KEYS.textMinLength, { count: rules.minLength }));
  }
  if (rules?.maxLength !== undefined && text.length > rules.maxLength) {
    problems.push(say('invalid', VALIDATION_KEYS.textMaxLength, { count: rules.maxLength }));
  }
  if (rules?.pattern !== undefined && !compilePattern(rules.pattern).test(text)) {
    if (rules.patternMessage !== undefined) {
      // A schema string, already translated by `localiseSchema`. Passed through, not re-resolved.
      problems.push({ kind: 'invalid', message: rules.patternMessage, messageKey: PATTERN_MESSAGE_FROM_SCHEMA });
    } else {
      problems.push(say('invalid', VALIDATION_KEYS.patternDefault));
    }
  }

  return problems;
}

/**
 * The `messageKey` on a problem whose text came from the schema's own `patternMessage`.
 *
 * A sentinel rather than a `ui.*` key, because there is no component string involved: the text is a
 * schema string with a `schema.*` key of its own. Marking it keeps "every `messageKey` a problem
 * carries is either a declared `ui.*` key or this sentinel" a checkable statement, instead of leaving a
 * key that looks declared but is not.
 */
export const PATTERN_MESSAGE_FROM_SCHEMA = 'schema:patternMessage';

export interface ValidateOptions {
  /** Restrict to one section — used to gate the Next button without nagging about later pages. */
  readonly sectionId?: string;
  readonly visibility?: Visibility;
  /**
   * The active locale's messages. Omit for English.
   *
   * ⚠️ Whatever supplies this must be the SAME locale the schema was localised with, or a technician
   * gets a Spanish question with an English refusal underneath it.
   */
  readonly messages?: Messages;
}

/** Every problem blocking submission, in document order. */
export function validate(schema: FormSchema, state: FormState, options: ValidateOptions = {}): Problem[] {
  const visibility = options.visibility ?? computeVisibility(schema, state);
  const messages = options.messages ?? englishMessages();
  const problems: Problem[] = [];

  for (const section of schema.sections) {
    if (options.sectionId !== undefined && section.id !== options.sectionId) continue;
    if (!visibility.sections.has(section.id)) continue;
    for (const field of section.fields) {
      if (!visibility.fields.has(field.id)) continue;
      for (const problem of checkField(field, state.entries[field.id]?.value, messages)) {
        problems.push({
          fieldId: field.id,
          sectionId: section.id,
          sectionTitle: section.title,
          label: field.label,
          ...problem,
        });
      }
    }
  }

  return problems;
}

export function canSubmit(schema: FormSchema, state: FormState, visibility?: Visibility): boolean {
  const opts: ValidateOptions = visibility ? { visibility } : {};
  return validate(schema, state, opts).length === 0;
}

/** Sections that still have problems, for the review page's jump-back links. */
export function sectionsWithProblems(problems: readonly Problem[]): { id: string; title: string; count: number }[] {
  const byId = new Map<string, { id: string; title: string; count: number }>();
  for (const p of problems) {
    const existing = byId.get(p.sectionId);
    if (existing) existing.count += 1;
    else byId.set(p.sectionId, { id: p.sectionId, title: p.sectionTitle, count: 1 });
  }
  return [...byId.values()];
}

export type { Section };
